import * as R from 'remeda'

import {
  calcNineSegmentsRatioAndIndicators,
  exists,
  mapNineSegment,
  type NineSegmentCounts,
  toNineNumber,
} from '~/utils'

import { type NineSegmentIndicator } from './nineSegments'

export type NineSegmentsBrandData = {
  brandId: number
  brandName: string
  brandNumber: number
  counts: NineSegmentCounts
  ratios: NineSegmentCounts & Record<NineSegmentIndicator, number>
}

export type NineSegmentsBrandsSortValue = {
  ascending: boolean
  columnKey: keyof NineSegmentsBrandData['ratios']
}

export type NineSegmentsBrandsDataInput = {
  // tabulationQueryData?.result ?? []. Engine basic-output emits the same
  // shape directly, so no reshape is needed.
  brands9segs: ReadonlyArray<{
    id: number
    name: string
    number: number
    result: ReadonlyArray<{ count: number; segmentNumber: number }>
  }>
  // useSampleSize() value. Replaces basic-output's per-brand sumBy inflation.
  sampleSize: number
  // From useAtomValue(nineSegmentSortValueAtom); basic-output passes undefined.
  sortValue: NineSegmentsBrandsSortValue | null | undefined
}

export const buildNineSegmentsBrandsData = (
  input: NineSegmentsBrandsDataInput,
): NineSegmentsBrandData[] =>
  R.pipe(
    input.brands9segs,
    R.map((brand9segs): NineSegmentsBrandData => {
      const nineSegmentCounts = mapNineSegment(
        (nineSegment) =>
          brand9segs.result.find(
            (segmentCount) =>
              segmentCount.segmentNumber === toNineNumber(nineSegment),
          )?.count ?? NaN,
      )

      return {
        brandId: brand9segs.id,
        brandName: brand9segs.name,
        brandNumber: brand9segs.number,
        counts: nineSegmentCounts,
        ratios: calcNineSegmentsRatioAndIndicators(
          nineSegmentCounts,
          input.sampleSize,
        ),
      }
    }),
    R.sortBy((item) =>
      exists(input.sortValue)
        ? item.ratios[input.sortValue.columnKey] *
          (input.sortValue.ascending ? 1 : -1)
        : 0,
    ),
  )
